import { Card, Skeleton, SkeletonButton, SkeletonRegion } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// The reception page's loading shapes. Only the day sheet is ever pending:
// the PageHeader, date navigator, KPI strip (`KpiCard loading`) and rail cards
// render for real around it (§"Independent loading states"), so these draw the
// two things that replace real rows — the desktop `AppointmentRow` block and
// the mobile `MobileAppointmentCard` list — at their real geometry.
// ─────────────────────────────────────────────────────────────────────────────

/** One `AppointmentRow` in placeholder: dot · time · divider · name/meta · chips. */
const AppointmentRowSkeleton = ({ i }) => (
  <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
    <Skeleton width={8} circle />
    <Skeleton width={44} height={13} />
    <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <Skeleton width={`${34 + ((i * 13) % 22)}%`} height={13} />
      <Skeleton width={`${22 + ((i * 7) % 18)}%`} height={11} />
    </div>
    <Skeleton width={64} height={20} radius={RADIUS.pill} />
    <SkeletonButton size="sm" width={84} />
  </div>
);

/**
 * Desktop day sheet: one `Block` (title strip + count) holding `rows`
 * appointment rows, inside the same borderless Card the real sheet uses.
 */
export const ReceptionDaySheetSkeleton = ({ rows = 4, label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="reception-day-skeleton">
      <Card style={{ padding: 0, border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div aria-hidden style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
          <Skeleton width={120} height={14} />
          <Skeleton width={56} height={20} radius={RADIUS.pill} />
        </div>
        {Array.from({ length: rows }, (_, i) => <AppointmentRowSkeleton key={i} i={i} />)}
      </Card>
    </SkeletonRegion>
  );
};

/** One `MobileAppointmentCard`: status chip + time on top, name, meta, an action row. */
const MobileAppointmentCardSkeleton = ({ i }) => (
  <Card aria-hidden style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Skeleton width={72} height={20} radius={RADIUS.pill} />
      <Skeleton width={44} height={14} />
    </div>
    <Skeleton width={`${48 + ((i * 17) % 30)}%`} height={15} />
    <Skeleton width={`${30 + ((i * 11) % 24)}%`} height={11} />
    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.125rem' }}>
      <SkeletonButton size="sm" style={{ flex: 1, width: 'auto' }} />
      <SkeletonButton size="sm" style={{ flex: 1, width: 'auto' }} />
    </div>
  </Card>
);

/** Mobile: the appointment card list under the pinned cockpit chrome. */
export const ReceptionMobileListSkeleton = ({ count = 3, label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="reception-list-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {Array.from({ length: count }, (_, i) => <MobileAppointmentCardSkeleton key={i} i={i} />)}
    </SkeletonRegion>
  );
};

export default ReceptionDaySheetSkeleton;
